/**
 * How a failed roster import POST ended, which decides whether the same file
 * may be sent again.
 *
 * - `report`: a 409, the import's own answer (rows to review, or rows that
 *   failed). The response says what happened.
 * - `unknown`: no response (a timeout or network failure) or a 5xx. The import
 *   may have run in full or in part: it commits row by row, so sending the file
 *   again could create each new player a second time.
 * - `rejected`: any other 4xx. The request was refused before anything was
 *   imported.
 */
export type ImportRequestOutcome = 'report' | 'unknown' | 'rejected'

const statusOf = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined
  }

  // req() puts a 4xx body's status on statusCode, and a 5xx's or a 504's on status.
  const { statusCode, status } = error as {
    statusCode?: unknown
    status?: unknown
  }
  const code = typeof statusCode === 'number' ? statusCode : status

  return typeof code === 'number' ? code : undefined
}

export const importRequestOutcome = (error: unknown): ImportRequestOutcome => {
  const status = statusOf(error)

  if (status === 409) {
    return 'report'
  }
  if (status === undefined || status >= 500) {
    return 'unknown'
  }

  return 'rejected'
}
